import { z } from 'zod';
import { MAX_RAPPEN } from './bau';
import { KANTON_KUERZEL } from './expose';
/**
 * Vermarktung — het FEED-CONTRACT als schema (band A-0/A-1).
 *
 * Normbron: `docs/architecture/VERMARKTUNG-FEED-CONTRACT.md` (v2 sinds 14-10). Dit bestand
 * is volgens §7 de **enige** bron van de feedvorm: de publisher (A-3) schrijft
 * uitsluitend `feedEnvelopeSchema.parse(...)`-uitvoer, de hahn-pull (A-5) parst
 * met `hahnAntwortSchema`, en de volledigheidspoort van de werkbank (A-2) is
 * `poortItemSchema` — hetzelfde schema minus de acht velden die pas ná de poort
 * bestaan. Afwijken van de vorm betekent: eerst het contract wijzigen (nieuwe
 * `contractVersion`), nooit stil hier.
 *
 * ── CONTRACT v2 (14-10, Marktstatus) ─────────────────────────────────────────
 * Het kauf-item kreeg `status`, `referenz` (beide verplicht), `veroeffentlichtAm`
 * en `kurzbeschreibung` (beide optioneel). Omdat de schema's recursief
 * `.strict()` zijn, is dat een ECHTE vormbreuk (contract §2.1: +1), dus:
 *  - SCHRIJVEN gebeurt uitsluitend in v2 (`feedEnvelopeSchema`/`feedManifestSchema`,
 *    `contractVersion: 2`);
 *  - de v1-vorm blijft BEVROREN bestaan onder `…V1`-namen, als LEESschema: een
 *    manifest of feedbestand dat vóór de omslag geschreven is (staging, het
 *    last-good miete-slot) moet de eigen opruimstap en grendel blijven halen;
 *  - LEZEN gebeurt met de leesunie (`feedEnvelopeLeesSchema`,
 *    `feedManifestLeesSchema`): versie 1 óf 2, en een ONBEKENDE versie faalt
 *    dicht.
 * `hahnItemSchema` blijft op de v1-bases: de ONYX-vorm beweegt niet.
 *
 * HUISREGELS DIE HIER GELDEN
 *  - **Recursief `.strict()`**: elk object, ook genest, weigert een onbekende
 *    sleutel; nergens `z.record()` of `.passthrough()`. `vermarktung-feed.test.ts`
 *    loopt de hele zod-boom af en pint dat (runtime, want
 *    `packages/shared/tsconfig.json` sluit testbestanden uit — een pin die
 *    alleen op tsc leunt draait nergens).
 *  - **Geld** = Int in Rappen, veldnaam op `…Rappen`, `0 ≤ x ≤ MAX_RAPPEN`
 *    (contract §3; MAX_RAPPEN komt uit `schemas/bau.ts`, één bovengrens).
 *  - **Geo** = `lat`/`lon` als STRING (contract §3). Dit is de eerste string-geo
 *    van het repo; de reden staat in het contract (bron is een Decimal-kolom,
 *    lezers zijn PHP én JS) en de conversie hoort op één plek: hier.
 *  - **Taal** (contract §2.2): envelop-, manifest- en beeldsleutels Engels;
 *    domeinvelden van een item Duits. Eén benoemde uitzondering in de beeldlaag:
 *    `titelbild`.
 *
 * BROWSER-VEILIG: dit bestand kent alleen zod en twee shared-constanten. Het
 * stempel-recept (§5) heeft `node:crypto` nodig en staat daarom apart in
 * `vermarktung-feed-stempel.ts`, dat alléén in de root-barrel hangt.
 */
// ───────────────────────────── Vocabulaire ──────────────────────────────────
/**
 * Contract §2.3 — de zeven waarden van de feed-enum. De checklist-enum
 * `IMMOBILIEN_TYPES` (`schemas/pipeline.ts`, vijf waarden) is hiervan een
 * DEELVERZAMELING, geen 1:1: `parkplatz`/`sonstiges` bestaan alleen in de
 * werkbank. `vermarktung-feed.test.ts` pint die deelverzameling-relatie.
 */
export const VERMARKTUNG_IMMOBILIEN_TYPEN = [
    'wohnung',
    'efh',
    'mfh',
    'gewerbe',
    'grundstueck',
    'parkplatz',
    'sonstiges',
];
export const vermarktungImmobilienTypSchema = z.enum(VERMARKTUNG_IMMOBILIEN_TYPEN);
/** Contract §1/§2.1 — de twee sleutelruimtes én de discriminator van het item. */
export const VERMARKTUNG_TRANSAKTIONEN = ['kauf', 'miete'];
export const feedTransaktionSchema = z.enum(VERMARKTUNG_TRANSAKTIONEN);
/**
 * Contract §2.3 — de drie beschikbaarheids-arten. Bewust een eigen lijst naast
 * `VERFUEGBARKEIT` in `schemas/pipeline.ts` (identieke waarden): de checklist is
 * dossier-invoer en de feed is een publiek artefact; ze mogen los bewegen. Dat
 * ze vandaag gelijk zijn is een CONTRACT-TOETS in `vermarktung-feed.test.ts`,
 * geen import.
 */
export const VERMARKTUNG_VERFUEGBARKEIT_ARTEN = [
    'sofort',
    'nach_vereinbarung',
    'per_datum',
];
export const vermarktungVerfuegbarkeitArtSchema = z.enum(VERMARKTUNG_VERFUEGBARKEIT_ARTEN);
/**
 * Contract §2.4 — GEAK-klasse A–G. Bewust gedupliceerd met de module-private
 * `geakLetter` van `schemas/pipeline.ts` (zelfde zeven letters): die is
 * dossier-invoer, deze is publieke artefact-vorm. `vermarktung-feed.test.ts`
 * pint dat het bij zeven letters blijft.
 */
export const VERMARKTUNG_ENERGIEETIKETTEN = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
export const energieetiketteSchema = z.enum(VERMARKTUNG_ENERGIEETIKETTEN);
/**
 * Contract v2 §2.4 (14-10) — de MARKTSTATUS van een kauf-object: drie waarden,
 * met opzet NIET `aktiv`. «aktiv» betekent in de werkbank al «online» (de
 * lijst-URL draagt `?status=aktiv|inaktiv` = Online/Offline), en «offline» is
 * iets anders dan «verkauft»: een verkocht object blijft een tijd MET de marke
 * online. Miete kent (nog) geen status — een lezer leest dan `verfuegbar`
 * (contract §10).
 */
export const VERMARKTUNG_STATUS = ['verfuegbar', 'reserviert', 'verkauft'];
export const vermarktungStatusSchema = z.enum(VERMARKTUNG_STATUS);
/**
 * De Duitse labels van de marktstatus — één tabel voor het CRM en elke andere
 * lezer die via `@finavest/shared/client` binnenkomt, zodat «Reserviert» niet
 * op drie plekken drie schrijfwijzen krijgt. `Object.freeze`: de pin draait
 * runtime (shared-toetsen zien tsc niet als bewijs).
 */
export const VERMARKTUNG_STATUS_LABEL = Object.freeze({
    verfuegbar: 'Verfügbar',
    reserviert: 'Reserviert',
    verkauft: 'Verkauft',
});
/** Contract §5 — de twee publieke beeldvarianten (breedte 1600 resp. 800). */
export const VERMARKTUNG_BILD_VARIANTEN = ['hero', 'card'];
/**
 * Contract §5 — versie van het BEELDRECEPT (varianten, breedtes, q78, EXIF,
 * metadata-strip). Zit in het stempel, dus:
 *
 * 🔴 **BUMPEN BIJ ELKE VISUELE WIJZIGING VAN HET RECEPT.** Een bump maakt alle
 * varianten opnieuw (nieuw stempel ⇒ nieuwe key ⇒ geen skip) en laat de oude
 * aan het opruim-hek (§6) over. Dit is de `EXPOSE_PDF_RENDERER_VERSION`-les:
 * een gewijzigde renderer met een ongewijzigde versie serveert oude bytes
 * onder een verse belofte.
 */
export const VERMARKTUNG_BILD_REZEPT_VERSION = 'V1';
// ─────────────────────────── Bouwstenen (§2.3/§3) ───────────────────────────
/** Contract §3 — geld: Int in Rappen, niet-negatief, ≤ BAU-`MAX_RAPPEN`. */
export const feedRappenSchema = z.number().int().nonnegative().max(MAX_RAPPEN);
/** Contract §2.3 — kebab-case slug, uniek binnen de transaktie. */
export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
/**
 * Contract §3 — datum zonder tijd. Maand `01`–`12`, dag `01`–`31`: een kale
 * `\d{2}-\d{2}` liet `2026-13-45` door, en die zou pas bij de LEZER stuklopen —
 * daar waar niemand van ons de fout meer ziet. (Schrikkeldagen en de lengte van
 * de maand blijven buiten de regex: dat is een kalender, geen vorm.)
 */
export const DATUM_RE = /^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/;
/** Contract §3 — ISO-8601 UTC MET milliseconden. */
export const ISO_MS_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
/**
 * Contract §3 — WGS84 als string, punt-scheider, 1 tot en met 6 decimalen,
 * **zonder leidende nul** (`01.5`).
 *
 * Dit is de ACCEPTATIE-grens bij intake, niet de canonisatie — de motivering
 * stond hier tot fixronde 3 verkeerd om. Gemeten: deze vorm laat `"46.94"`
 * én `"46.940000"` door, twee schrijfwijzen van hetzelfde punt en dus twee
 * `<sha16>`. Wat de feed canoniek maakt is de PRODUCENT: die volgt het recept
 * van §3 ({@link geoZahlUit}) en schrijft altijd zes decimalen. De regex houdt
 * de ruimte daaromheen klein zonder de hahn-invoer (fase B) op één schrijfwijze
 * vast te pinnen.
 */
export const GEO_ZAHL_RE = /^-?(?:0|[1-9]\d{0,2})\.\d{1,6}$/;
/**
 * Een APARTE toets met een eigen foutcode (`negative_null`), want de vorm
 * hierboven laat hem door (`GEO_ZAHL_RE.test('-0.0') === true`): `-0.000000` is
 * dezelfde coördinaat als `0.000000`, en twee schrijfwijzen zijn twee hashes.
 */
const NEGATIEVE_NUL_RE = /^-0\.0+$/;
/**
 * Fixronde 3 — het geo-recept van §3 heeft TWEE stappen, en de producent moet ze
 * allebei doen: `toFixed(6)` én het minteken wegstrepen van een uitkomst die uit
 * `-0.` plus louter nullen bestaat.
 *
 * Gemeten met alleen stap één: `lon: -0.0000004` ⇒ `"-0.000000"` ⇒
 * `negative_null` ⇒ poort-gebrek `geo.lon` ⇒ het object kan NOOIT aktiv worden,
 * zonder dat de staf iets kan corrigeren (de coördinaat komt uit de geocode).
 * Buiten Zwitserland niet bereikbaar, maar A-6 laat hetzelfde recept op
 * ONYX-adressen los.
 *
 * Woont HIER en niet bij de producent: de vorm, de negatieve-nul-regel en het
 * recept dat er precies in past horen op één plek te staan, anders drijven ze
 * uit elkaar.
 */
export function geoZahlUit(waarde) {
    const tekst = waarde.toFixed(6);
    return NEGATIEVE_NUL_RE.test(tekst) ? tekst.slice(1) : tekst;
}
/** Volledige sha256 in kleine letters (manifest-integriteit, §4.2). */
export const SHA256_RE = /^[0-9a-f]{64}$/;
/**
 * Contract v2 §2.4 — de publieke OBJEKTREFERENZ: twee keer vier HOOFDLETTER-hex
 * met een streepje (`3F2A-91C0`). Een leesbare verwijzing voor de koper aan de
 * telefoon, GEEN sleutel: hij is nooit `rimoNr` (dat is een verboden veld, §7)
 * en niet gegarandeerd uniek (32 bits uit de object-id; bij 1'000 objecten is
 * de kans op een dubbele ≈ 0,01 %). Zonder `g`-vlag, zoals elke regex hier.
 */
export const REFERENZ_RE = /^[0-9A-F]{4}-[0-9A-F]{4}$/;
/**
 * Owner-besluit O-4 (14-10) — de referenz komt UIT DE OBJECT-ID: de eerste acht
 * hex-tekens zonder streepjes, in hoofdletters, als `XXXX-XXXX`. De ENIGE plek
 * waar hij ontstaat (zelfde regel als {@link feedKeyUit}): de uitkomst wordt
 * tegen {@link REFERENZ_RE} getoetst en GOOIT als hij er niet in past — een id
 * die geen hex is (het werkbank-object draagt een uuid, dus onbereikbaar) mag
 * geen referenz opleveren die een lezer daarna afwijst.
 */
export function referenzUit(id) {
    const hex = id.replace(/-/g, '').slice(0, 8).toUpperCase();
    const referenz = `${hex.slice(0, 4)}-${hex.slice(4, 8)}`;
    if (!REFERENZ_RE.test(referenz)) {
        throw new Error(`vermarktung_referenz_ungueltig: ${id}`);
    }
    return referenz;
}
export const feedZeitstempelSchema = z.string().regex(ISO_MS_RE);
/**
 * Contract §2.3 — "tekst zonder markup/entities". Bewust NAUW: alleen een
 * tag-opener (`<` gevolgd door letter of `/`) en HTML-entities worden geweigerd,
 * zodat een legitieme `>`-vergelijking of een losse `&` in een beschrijving
 * blijft werken. De feed is de bron voor een WordPress-plugin die de tekst als
 * platte tekst rendert; markup zou daar of dubbel-escaped of uitgevoerd worden.
 */
const MARKUP_RE = /<[a-zA-Z/!]|&(?:[a-zA-Z][a-zA-Z0-9]{1,10}|#[0-9]{1,7}|#x[0-9a-fA-F]{1,6});/;
const geenMarkup = (s) => !MARKUP_RE.test(s);
/**
 * Order PR-2 (15-10, A3): dezelfde markup-regel als predicaat, voor de nabewerking
 * van een KI-voorstel — een voorgestelde tekst met markup wordt daar GESPERRD,
 * met exact de regel waarmee de feed hem later zou weigeren (één regel, geen
 * tweede kopie die kan afdrijven).
 */
export const enthaeltMarkup = (s) => MARKUP_RE.test(s);
/** Contract §2.3 — flächen in m², max 1 decimaal. */
const eenDecimaal = (v) => Math.abs(v * 10 - Math.round(v * 10)) < 1e-6;
const m2Schema = z.number().nonnegative().max(1_000_000).refine(eenDecimaal, {
    error: 'flaeche_max_1_dezimale',
});
/** Contract §2.3 — halve kamers toegestaan (`3.5`), niets fijner. */
const halveStap = (v) => Math.abs(v * 2 - Math.round(v * 2)) < 1e-6;
/**
 * Contract §7 — `kanton` = `z.enum(KANTON_KUERZEL)`, de ENE kantonlijst van
 * shared (`schemas/expose.ts`, afgeleid uit `KAUFNEBENKOSTEN_RATES`). Geen
 * tweede literal in dit bestand.
 */
export const kantonSchema = z.enum(KANTON_KUERZEL);
/**
 * Contract §2.3 — het publieke adres, **verplicht en niet-nulbaar** in het item
 * (owner-besluiten 10-9: kauf straat altijd publiek, huur adres publiek). Alle
 * vier de velden verplicht; beide producenten normaliseren vóór de poort en de
 * poort weigert al het andere.
 *
 * Bewust NIET de checklist-`addressSchema` uit `schemas/pipeline.ts`: die is
 * `.partial()`, `plz` is daar een kale string en `kanton` een 2-tekenstring
 * zonder hoofdletter-eis — dossier-invoer mag niet hard falen, een publiek
 * artefact wél. (Die schema is bovendien module-privé.)
 */
export const adresseSchema = z
    .object({
    strasse: z.string().trim().min(1).max(120),
    plz: z.string().regex(/^\d{4}$/),
    ort: z.string().trim().min(1).max(80),
    kanton: kantonSchema,
})
    .strict();
/**
 * Contract §3 — de exacte gebouwcoördinaat, als STRINGS. Geen `genauigkeit` en
 * geen `praezision`: sinds V-4 is er geen vervaging en die velden zouden geen
 * producent hebben.
 */
const geoZahlSchema = (grens, buitenBereik) => z
    .string()
    .regex(GEO_ZAHL_RE)
    .refine((s) => !NEGATIEVE_NUL_RE.test(s), { error: 'negative_null' })
    .refine((s) => {
    const n = Number(s);
    // ÉÉN FOUT, ÉÉN REDEN (master-verdict #553, MINOR-1). Een VORMfout is al
    // gemeld door de regex hierboven; zonder deze eerste voorwaarde draait de
    // bereiktoets door op `Number('46,9')` = `NaN` en meldt óók nog
    // «buiten bereik» — één schrijffout met twee redenen. ONYX B-4 leest de
    // redenenverzameling en niet de eerste, en zou dan een `custom` zien die
    // «buiten bereik» beweert terwijl het probleem de schrijfwijze is
    // (contract §3). `Number('')` is 0 en dus eindig, dus de lege string
    // blijft zoals hij was: alleen `invalid_format`.
    return !Number.isFinite(n) || (n >= -grens && n <= grens);
}, { error: buitenBereik });
export const geoSchema = z
    .object({
    lat: geoZahlSchema(90, 'lat_ausserhalb_bereich'),
    lon: geoZahlSchema(180, 'lon_ausserhalb_bereich'),
})
    .strict();
/** Contract §2.3 — `{ art, datum? }`; de checklist kent geen datumveld, de feed wel. */
export const vermarktungVerfuegbarkeitSchema = z
    .object({
    art: vermarktungVerfuegbarkeitArtSchema,
    datum: z.string().regex(DATUM_RE).optional(),
})
    .strict();
/** Contract §2.3 — vlakken in m² (max 1 decimaal), alle vier optioneel. */
export const flaechenSchema = z
    .object({
    wohnflaecheM2: m2Schema.optional(),
    nutzflaecheM2: m2Schema.optional(),
    grundstuecksflaecheM2: m2Schema.optional(),
    balkonflaecheM2: m2Schema.optional(),
})
    .strict();
/**
 * De gevoelige veldnamen — de UNIE VAN BEIDE KANTEN, één lijst. Geen van deze
 * namen mag als sleutel voorkomen in de projectie, het feed-item of het
 * hahn-item — recursief gemeten over de zod-boom, niet per veld
 * (`vermarktung.test.ts`, mandanten-vormpin-klasse).
 *
 * BEIDE KANTEN, en dus NIET «de exacte namen uit ontwerp §1»: contract §7 legt
 * de wacht vast als «beide kanten, recursief» en somt de miete-helft met zoveel
 * woorden op. Ontwerp §1 beschrijft de kauf-IST van het platform-dossier; waar
 * de twee elkaar raken wint het contract.
 *  - **kauf** (ontwerp §1 «Gevoelig», de eerste elf) — de dossiervelden van het
 *    Maklermandat.
 *  - **miete/verwaltung** (de laatste acht) — `rimoNr`, `amtlicherWert`,
 *    `kaufpreis` en `wertquote` staan verbatim in §7; `gvbWert`,
 *    `baurechtszins`, `stweEigentuemerId` en `stweEigentuemer` staan in de
 *    «wat NOOIT publiek wordt»-lijst van het fase-B-ontwerp en zijn echte
 *    ONYX-veldnamen. Ze horen HIER en niet in een toets, omdat B-8 het
 *    ONYX-object mapt en dit bestand niet kan importeren (INV-2): is de lijst
 *    onvolledig, dan is de wacht dat ook — en dan staat de fout pas in
 *    `listings/miete/feed-<n>-<sha16>.json`.
 *
 * De PROZA-categorie van §7 («mieter- en vertragsgegevens») noemt geen namen en
 * is niet opsombaar; die wordt gedekt door {@link VERBOTEN_TOKENS} hieronder.
 *
 * WOONT HIER, in het contract-bestand, en niet in `schemas/vermarktung.ts`:
 * contract §7 legt de verboden-veldenwacht «beide kanten, recursief» vast, en
 * {@link merkmalSchema} hieronder — het ENE vrije sleutelveld van de hele vorm —
 * moet de lijst kunnen lezen. Andersom zou een import-cyclus ontstaan
 * (`vermarktung.ts` importeert dit bestand, nooit omgekeerd).
 *
 * `Object.freeze` en niet alleen `as const`: `packages/shared/tsconfig.json`
 * sluit testbestanden uit, dus een pin die alleen op tsc leunt zou nergens
 * draaien (gemeten — zie de kop van `mandanten.ts`).
 */
export const VERBOTENE_FELDER = Object.freeze([
    'eigentuemerName',
    'eigentuemerHandy',
    'eigentuemerEmail',
    'eigentuemerAdresse',
    'mindestverkaufspreis',
    'grundbuchNr',
    'schaetzwertHedonisch',
    'eigenmietwert',
    'commissionSide',
    'achievedPrice',
    'honorarPct',
    'rimoNr',
    'amtlicherWert',
    'kaufpreis',
    'wertquote',
    'gvbWert',
    'baurechtszins',
    'stweEigentuemerId',
    'stweEigentuemer',
]);
/**
 * De tweede helft van de wacht van §7: de niet-opsombare PROZA-categorie. Die
 * noemt in het contract geen namen, en de ONYX-veldnamen erachter zijn hier niet
 * importeerbaar (INV-2) — een namenlijst zou dus per constructie onvolledig
 * zijn en het gat zou pas in de feed zichtbaar worden. Daarom een casefold
 * SUBSTRING-toets op de sleutelnaam, NÁÁST {@link VERBOTENE_FELDER}: hij dekt
 * `mieterName`, `hauptmieter`, `vermieter`, `mietvertrag`, `vertragsbeginn`
 * enzovoort zonder ze te hoeven kennen.
 *
 * VIER TOKENS sinds fixronde 3, niet twee. `mieter` en `vertrag` dekken de
 * §7-categorie «mieter- en vertragsgegevens»; `eigentuemer` en `notiz` komen uit
 * de «wat NOOIT publiek wordt»-lijst van het fase-B-ontwerp — dezelfde bron die
 * ook `gvbWert` en `stweEigentuemer` in de namenlijst hierboven zette — en
 * generaliseren de vier §7-kaufnamen (`eigentuemerName/-Handy/-Email/-Adresse`)
 * naar de vormen die een producent wél kan kiezen. GEMETEN zonder die twee:
 * `eigentuemer_telefon`, `eigentuemerin`, `stwe_eigentuemer`, `notiz` en
 * `notizen` kwamen er alle vijf doorheen.
 *
 * Gecontroleerd op valse treffers over de publieke contractvelden: geen ervan
 * draagt `eigentuemer` of `notiz` als substring — `stockwerkeigentum` en
 * `eigentumsform` bevatten `eigentum`, niet `eigentuemer`.
 *
 * BEWUST APART BENOEMD en niet in de namenlijst gemengd: de ene lijst is een
 * gelijkheids-toets, deze is een substring-toets, en een lezer die de twee door
 * elkaar haalt leest de reikwijdte verkeerd.
 *
 * REIKWIJDTE = de PRODUCENT-GEKOZEN sleutelnaam (`merkmale[].schluessel`), niet
 * de sleutels van dit bestand. Gemeten: de contract-eigen miete-velden
 * `nettomieteRappen` en `bruttomieteRappen` dragen `mieter` als substring door
 * pure samenstelling (`miete` + `Rappen`), terwijl §2.4 ze juist als PUBLIEKE
 * huurprijs voorschrijft. Een substring-regel over de schema-sleutels zou die
 * twee weigeren; over de vrij gekozen `schluessel` is hij precies wat §7 vraagt.
 */
export const VERBOTEN_TOKENS = Object.freeze([
    'mieter',
    'vertrag',
    'eigentuemer',
    'notiz',
]);
/**
 * Fixronde 3 — de ÉNE normalisatie van de wacht, aan BEIDE kanten: casefold én de
 * underscores weg.
 *
 * Casefold alleen was tandeloos, en dat is gemeten: `merkmalSchema.schluessel`
 * moet `^[a-z0-9_]+$` matchen, dus camelCase KÁN daar niet staan — snake_case is
 * de vorm die de regex uitnodigt. Een set van `naam.toLowerCase()` tegen een
 * `schluessel.toLowerCase()` liet daarom 14 van de 19 namen door in hun
 * snake_case-vorm (`eigentuemer_name`, `grundbuch_nr`, `rimo_nr`,
 * `stwe_eigentuemer`, `commission_side`, …): de wacht bewaakte precies de
 * schrijfwijze die de producent niet eens kan kiezen. Door aan beide kanten de
 * underscores te verwijderen valt `eigentuemer_name` samen met
 * `eigentuemerName` en dekt één lijst beide vormen.
 *
 * Dezelfde normalisatie vóór de TOKEN-toets, om dezelfde reden: een sleutel die
 * het token zelf met een underscore breekt (`ver_trag`, `mie_ter`) komt er
 * anders langs.
 */
const normaliseerSleutel = (naam) => naam.toLowerCase().replace(/_/g, '');
const VERBOTEN_CASEFOLD = new Set(VERBOTENE_FELDER.map(normaliseerSleutel));
/**
 * Contract §2.3 — vrije merkmaal-paren; sleutel is een bounded token.
 *
 * `merkmale[].schluessel` is de ENE plek waar een producent zelf een SLEUTELNAAM
 * kiest, en daarmee het ene gat in de naam-allowlist: `{schluessel:
 * 'mindestverkaufspreis', wert: '1190000'}` draagt de verboden naam én de
 * verboden waarde en zou anders door élk schema komen (gemeten). De wacht van
 * §7 geldt daarom óók hier — en omdat de projectie, het feed-item en het
 * hahn-item alle drie dit ene schema gebruiken, geldt hij in één keer aan beide
 * kanten.
 *
 * Twee toetsen, want §7 noemt twee categorieën: de NAMENLIJST
 * ({@link VERBOTENE_FELDER}, gelijkheid) en de niet-opsombare proza-categorie
 * ({@link VERBOTEN_TOKENS}, substring). Elk met een eigen `message`, zodat een
 * afwijzing leesbaar blijft.
 */
export const merkmalSchema = z
    .object({
    schluessel: z
        .string()
        .max(60)
        .regex(/^[a-z0-9_]+$/),
    wert: z.string().trim().min(1).max(200),
})
    .strict()
    .superRefine((merkmal, ctx) => {
    const sleutel = normaliseerSleutel(merkmal.schluessel);
    if (VERBOTEN_CASEFOLD.has(sleutel)) {
        ctx.addIssue({ code: 'custom', path: ['schluessel'], message: 'verbotenes_feld' });
    }
    if (VERBOTEN_TOKENS.some((token) => sleutel.includes(token))) {
        ctx.addIssue({ code: 'custom', path: ['schluessel'], message: 'verbotenes_token' });
    }
});
/** Contract §5 — één variant van een publiek beeld (absolute URL + echte maten). */
const bildVarianteSchema = z
    .object({
    url: z
        .string()
        .max(2048)
        .regex(/^https?:\/\/\S+$/),
    width: z.number().int().positive().max(20_000),
    height: z.number().int().positive().max(20_000),
})
    .strict();
/**
 * Contract §5 — het ARTEFACT-beeld-item (wat in de feed staat). Dezelfde
 * sleutelnamen als het hahn-item hieronder; `alt` is nooit leeg (werkbank-alt →
 * caption → titel) en `titelbild` is de ene bewust Duitse sleutel in de
 * beeldlaag (§2.2).
 */
export const bildItemSchema = z
    .object({
    alt: z.string().trim().min(1).max(300),
    titelbild: z.boolean(),
    hero: bildVarianteSchema,
    card: bildVarianteSchema,
})
    .strict();
/**
 * Contract §5/§2.6 — het HAHN-beeld-item (wat ONYX over de hahn levert):
 * `sha256` van het origineel, een hahn-RELATIEF pad zonder herkomst (het
 * platform zet zijn ONYX-basis ervoor), en de maten van het ORIGINEEL.
 */
export const hahnBildItemSchema = z
    .object({
    sha256: z.string().regex(SHA256_RE),
    url: z
        .string()
        .max(2048)
        .regex(/^\/\S*$/),
    width: z.number().int().positive().max(60_000),
    height: z.number().int().positive().max(60_000),
    alt: z.string().trim().min(1).max(300),
    titelbild: z.boolean(),
})
    .strict();
/**
 * Contract §2.3 — "precies één `titelbild: true` als er bilder zijn". Een lege
 * lijst is geldig (een item zonder foto's haalt de werkbank-poort niet, maar de
 * feedvorm zelf verbiedt hem niet — dat onderscheid hoort bij
 * `pruefeKomplettheit`, niet hier).
 */
const bilderLijst = (element) => z
    .array(element)
    .max(60)
    .superRefine((lijst, ctx) => {
    if (lijst.length === 0)
        return;
    const titelbilder = lijst.filter((b) => b.titelbild).length;
    if (titelbilder !== 1) {
        ctx.addIssue({ code: 'custom', message: 'genau_ein_titelbild' });
    }
});
// ───────────────────── Basisobjecten (contract §2.3 + §2.4) ─────────────────
/**
 * De gemeenschappelijke kern van kauf én miete (§2.3). Uit dit ene object
 * ontstaan de twee basisobjecten, en uit die twee de DRIE unions (artefact,
 * hahn, poort) — zo kunnen poort en artefact niet driften (§7).
 */
const kernSchema = z
    .object({
    id: z.string().min(1).max(64),
    slug: z.string().min(1).max(160).regex(SLUG_RE),
    titel: z.string().trim().min(1).max(160).refine(geenMarkup, { error: 'markup_verboten' }),
    beschreibung: z
        .string()
        .trim()
        .min(1)
        .max(20_000)
        .refine(geenMarkup, { error: 'markup_verboten' }),
    highlights: z.array(z.string().trim().min(1).max(200)).max(10),
    immobilienTyp: vermarktungImmobilienTypSchema,
    zimmer: z
        .number()
        .nonnegative()
        .max(100)
        .refine(halveStap, { error: 'halbe_zimmer' })
        .nullable(),
    flaechen: flaechenSchema,
    baujahr: z.number().int().min(1000).max(2200).nullable(),
    stockwerk: z.string().trim().min(1).max(60).nullable(),
    verfuegbarkeit: vermarktungVerfuegbarkeitSchema,
    adresse: adresseSchema,
    geo: geoSchema,
    merkmale: z.array(merkmalSchema).max(50),
    stand: feedZeitstempelSchema,
})
    .strict();
/**
 * Contract §2.4 (kauf) — `preisRappen` is **afwezig** (niet `0`, niet `null`) als
 * `preisAufAnfrage: true`, en verplicht als hij `false` is. Beide richtingen:
 * een object dat noch een prijs noch "op aanvraag" draagt, is geen publicabel
 * kauf-object.
 */
const preisRegel = (waarde, ctx) => {
    if (waarde.preisAufAnfrage && waarde.preisRappen !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['preisRappen'], message: 'preis_bei_anfrage_verboten' });
    }
    if (!waarde.preisAufAnfrage && waarde.preisRappen === undefined) {
        ctx.addIssue({ code: 'custom', path: ['preisRappen'], message: 'preis_fehlt' });
    }
};
/**
 * Contract v2 §2.4 (O-10) — STATUS VÓÓR PREIS: een `verkauft`-item draagt GEEN
 * `preisRappen`. Bij `reserviert` mag de prijs blijven staan (een reservering
 * is geen verkoop). Samen met {@link preisRegel} betekent dat: een verkocht
 * object staat in de feed met `preisAufAnfrage: true` en zonder bedrag — de
 * publisher (`itemUit`) schrijft precies die vorm, deze regel weigert elke
 * andere.
 */
const statusPreisRegel = (waarde, ctx) => {
    if (waarde.status === 'verkauft' && waarde.preisRappen !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['preisRappen'], message: 'preis_bei_verkauft_verboten' });
    }
};
/**
 * Basisobject KAUF in de BEVROREN v1-vorm (§2.3 + §2.4 kauf, `contractVersion: 1`).
 *
 * 🔒 BYTE-GELIJK AAN DE VORM VAN VÓÓR 14-10 EN NIET MEER TE WIJZIGEN: dit is
 * het LEESschema voor bestanden die in v1 geschreven zijn, plus de basis van
 * `hahnItemSchema` (de ONYX-vorm beweegt niet mee met v2). Wie hier een veld
 * toevoegt, maakt van een geldig v1-bestand een onleesbaar bestand.
 */
export const vermarktungKaufBasisSchemaV1 = kernSchema
    .extend({
    transaktion: z.literal('kauf'),
    preisRappen: feedRappenSchema.optional(),
    preisAufAnfrage: z.boolean(),
    energieetikette: energieetiketteSchema.nullable(),
    exposeAnfrage: z.boolean(),
    bilder: bilderLijst(bildItemSchema),
})
    .strict();
/**
 * Basisobject KAUF, contract v2 (§2.4 kauf, 14-10): de v1-vorm plus vier velden.
 *  - `status` — VERPLICHT, de marktstatus ({@link VERMARKTUNG_STATUS});
 *  - `referenz` — VERPLICHT, {@link REFERENZ_RE} (O-4: uit de object-id);
 *  - `veroeffentlichtAm` — optioneel, ISO-ms; = `publiziertAt` (O-5: een
 *    heractivering is weer «neu»);
 *  - `kurzbeschreibung` — optioneel, 1–300 tekens, zonder markup (de teaser
 *    naast `beschreibung`).
 * NIET in de feed: `statusSeit` (master-besluit c). Het venster van de
 * «Verkauft»-weergave woont uitsluitend bij de producent; voor een lezer is
 * AFWEZIGHEID uit de feed de enige eindregel (contract §10).
 */
export const vermarktungKaufBasisSchema = vermarktungKaufBasisSchemaV1
    .extend({
    status: vermarktungStatusSchema,
    veroeffentlichtAm: feedZeitstempelSchema.optional(),
    referenz: z.string().regex(REFERENZ_RE),
    kurzbeschreibung: z
        .string()
        .trim()
        .min(1)
        .max(300)
        .refine(geenMarkup, { error: 'markup_verboten' })
        .optional(),
})
    .strict();
/** Basisobject MIETE (§2.3 + §2.4 miete), met het artefact-beeld-item. */
export const vermarktungMieteBasisSchema = kernSchema
    .extend({
    transaktion: z.literal('miete'),
    nettomieteRappen: feedRappenSchema,
    nkAkontoRappen: feedRappenSchema,
    bruttomieteRappen: feedRappenSchema,
    waehrung: z.literal('CHF'),
    bilder: bilderLijst(bildItemSchema),
})
    .strict();
// ─────────────────────── De drie unions (contract §7) ───────────────────────
/**
 * Het item zoals het IN DE FEED staat (artefact-beelden), contract v2 — de ENE
 * vorm die de publisher schrijft. Kauf draagt twee regels: de prijsregel van
 * §2.4 en «status vóór preis» (O-10).
 */
export const feedItemSchema = z.discriminatedUnion('transaktion', [
    vermarktungKaufBasisSchema.superRefine(preisRegel).superRefine(statusPreisRegel),
    vermarktungMieteBasisSchema,
]);
/** Het item in de BEVROREN v1-vorm — uitsluitend om v1-bestanden te LEZEN. */
export const feedItemSchemaV1 = z.discriminatedUnion('transaktion', [
    vermarktungKaufBasisSchemaV1.superRefine(preisRegel),
    vermarktungMieteBasisSchema,
]);
/**
 * Het item zoals ONYX het over de hahn levert (§2.6): de v1-vorm met
 * hahn-beelden. BEWUST op de v1-bases en niet op v2: de hahn levert alleen miete
 * en de ONYX-vorm beweegt niet mee met de kauf-velden van v2 (14-10).
 */
export const hahnItemSchema = z.discriminatedUnion('transaktion', [
    vermarktungKaufBasisSchemaV1
        .extend({ bilder: bilderLijst(hahnBildItemSchema) })
        .superRefine(preisRegel),
    vermarktungMieteBasisSchema.extend({ bilder: bilderLijst(hahnBildItemSchema) }),
]);
/**
 * Het antwoordlichaam van de hahn (§2.6): een OBJECT-omhulsel, zodat er later
 * `erzeugtAm`/`anzahl` bij kan zonder breuk. Nooit een kale `z.array`.
 *
 * ⚠️ Die twee namen zijn met de A-5-nachtrag (§2.6a) gecorrigeerd van
 * `generatedAt`/`count` naar Duits: dit lichaam is een EIGEN laag en geen
 * §2.1-envelop, en §2.2 verbiedt mengen binnen één laag — `objekte` is Duits,
 * dus de rest ook.
 *
 * `aktiveQuellobjekte` (§2.6a, OPTIONEEL) draagt het aantal FREIGEGEBEN-rijen
 * VÓÓR de projectie-filtering. Zonder dat getal is de nul-uit-niet-nul-poort
 * (§7) aan de miete-kant onmeetbaar: de publisher ziet daar geen tabel maar
 * alleen een lijst, en een lege lijst is van buiten niet te onderscheiden van
 * «alles afgewezen» of «bron stuk».
 *
 * 🔴 WAAROM HIJ HIER AL STAAT TERWIJL ONYX HEM NOG NIET STUURT. Dit schema is
 * recursief `.strict()`: een onbekende sleutel wordt AFGEWEZEN, niet genegeerd.
 * Zou de producent het veld eerder sturen dan de lezer het kent, dan faalt élk
 * antwoord op de parse en valt het hele miete-slot uit. De lezer accepteert het
 * dus eerst; pas daarna mag ONYX het sturen (B-8-1). Optioneel blijft hij ook
 * daarna — de afwezigheid is een geldige toestand met een eigen uitkomst
 * («onmeetbaar»), geen fout.
 */
export const hahnAntwortSchema = z
    .object({
    objekte: z.array(hahnItemSchema),
    aktiveQuellobjekte: z.number().int().min(0).optional(),
})
    .strict();
/**
 * **De volledigheidspoort van de werkbank IS dit schema** (contract §7): het
 * feed-item minus de acht velden die pas ná de poort bestaan — `id`, `slug`,
 * `stand`, `status`, `veroeffentlichtAm` en `referenz` (alle zes uit de rij),
 * `exposeAnfrage` (exposé-stand) en `bilder` (gerenderde varianten). Bij miete
 * bestaan `exposeAnfrage` en de drie v2-rijvelden niet, dus daar wordt alleen
 * weggelaten wat er is. `kurzbeschreibung` blijft ERIN: die komt uit de
 * projectie en hoort dus gemeten te worden.
 *
 * Uit DEZELFDE basisobjecten opgebouwd als `feedItemSchema`, dus poort en
 * artefact kunnen niet driften. Een kale `safeParse(projektion)` of een
 * `safeParse` van de volle vorm vóór de render kan nooit slagen en is geen
 * geldige lezing.
 */
export const poortItemSchema = z.discriminatedUnion('transaktion', [
    vermarktungKaufBasisSchema
        .omit({
        id: true,
        slug: true,
        stand: true,
        exposeAnfrage: true,
        bilder: true,
        status: true,
        veroeffentlichtAm: true,
        referenz: true,
    })
        .superRefine(preisRegel),
    vermarktungMieteBasisSchema.omit({ id: true, slug: true, stand: true, bilder: true }),
]);
/**
 * De ACHT velden die `poortItemSchema` weglaat — de na-poort-bronnen (§7). Vijf
 * sinds v1, drie sinds v2 (14-10): de marktstatus, de publicatiedatum en de
 * referenz komen alle drie uit de RIJ en niet uit de projectie.
 */
export const NA_POORT_VELDEN = Object.freeze([
    'id',
    'slug',
    'stand',
    'exposeAnfrage',
    'bilder',
    'status',
    'veroeffentlichtAm',
    'referenz',
]);
// ─────────────────────────── Envelop (contract §2.1) ────────────────────────
/**
 * De twee envelop-regels van §2.1, ÉÉN keer opgeschreven voor alle drie de
 * envelopschema's (v1, v2, leesunie): `count` is `items.length`, en elk item
 * hoort bij de transaktie van zijn envelop.
 */
const envelopRegel = (envelop, ctx) => {
    if (envelop.count !== envelop.items.length) {
        ctx.addIssue({ code: 'custom', path: ['count'], message: 'count_ungleich_items' });
    }
    for (let i = 0; i < envelop.items.length; i += 1) {
        const item = envelop.items[i];
        if (item !== undefined && item.transaktion !== envelop.transaktion) {
            ctx.addIssue({
                code: 'custom',
                path: ['items', i, 'transaktion'],
                message: 'transaktion_ungleich_envelope',
            });
        }
    }
};
/**
 * Contract §2.1 — één feedbestand in de BEVROREN v1-vorm. Uitsluitend om te
 * LEZEN wat vóór 14-10 geschreven is (zie de kop van dit bestand); geen enkele
 * schrijver maakt nog een v1-bestand.
 */
export const feedEnvelopeSchemaV1 = z
    .object({
    contractVersion: z.literal(1),
    transaktion: feedTransaktionSchema,
    generation: z.number().int().nonnegative(),
    generatedAt: feedZeitstempelSchema,
    count: z.number().int().nonnegative(),
    items: z.array(feedItemSchemaV1).max(10_000),
})
    .strict()
    .superRefine(envelopRegel);
/**
 * Contract §2.1 — één feedbestand, het SCHRIJFschema (v2). De feed is
 * **volledig, nooit een delta**; een lege feed is een geldig bestand met
 * `count: 0`, maar alleen als de bron écht nul aktive objecten heeft (§7
 * nul-uit-niet-nul is een publisher-regel, geen schemaregel).
 */
export const feedEnvelopeSchema = z
    .object({
    contractVersion: z.literal(2),
    transaktion: feedTransaktionSchema,
    generation: z.number().int().nonnegative(),
    generatedAt: feedZeitstempelSchema,
    count: z.number().int().nonnegative(),
    items: z.array(feedItemSchema).max(10_000),
})
    .strict()
    .superRefine(envelopRegel);
/**
 * De LEESUNIE van het feedbestand (contract §10): versie 1 óf 2, en de items
 * uit de unie van beide itemvormen. De eigen lezers — de opruimstap van de
 * publisher (§6) — lopen hiermee over de omslag heen: een v1-feed waar een
 * last-good slot nog naar wijst moet zonder worp gelezen worden, anders krimpt
 * de referentieverzameling en wist het hek levende beelden.
 *
 * BEWUST TOLERANT OP DE COMBINATIE, STRENG OP DE VERSIE. De versie en de
 * itemvorm worden hier niet aan elkaar gekoppeld (een v2-item in een bestand
 * met `contractVersion: 1` leest); dat kan alleen een schrijver maken die het
 * SCHRIJFschema hierboven omzeilt, en een lezer die daar alsnog over struikelt
 * zou zijn opruimstap laten vallen zonder dat er iets aan de feed mis is. Wat
 * NOOIT leest is een onbekende versie: `contractVersion: 3` faalt dicht.
 */
export const feedEnvelopeLeesSchema = z
    .object({
    contractVersion: z.union([z.literal(1), z.literal(2)]),
    transaktion: feedTransaktionSchema,
    generation: z.number().int().nonnegative(),
    generatedAt: feedZeitstempelSchema,
    count: z.number().int().nonnegative(),
    items: z.array(z.union([feedItemSchemaV1, feedItemSchema])).max(10_000),
})
    .strict()
    .superRefine(envelopRegel);
// ────────────────────────── Sleutelruimte (contract §1) ─────────────────────
//
// §1 sluit de sleutelruimte onder `listings/`: het manifest, de twee
// feedbestanden en de beeldvarianten — «geen andere keys». Die belofte is alleen
// waar zolang er ÉÉN plek is die de namen maakt; daarom staan de regexes en de
// bouwers hier bij elkaar en niet verspreid over publisher en lezer.
//
// Precedent: `planUploads` (`packages/geo-pipeline/src/artifacts.ts`) — daar IS
// die ene functie het artefact-naam-contract, en de contract-toets eist dat de
// kaart nooit een key haalt die de functie niet produceert. Hier gaat dezelfde
// eigenschap een stap verder: elke bouwer toetst zijn EIGEN uitkomst tegen de
// regex die de lezerskant gebruikt en gooit als die faalt. Een key die de regex
// zou afwijzen kan dus niet ontstaan — de twee richtingen kunnen niet driften,
// ook niet als niemand er een toets voor schrijft.
/**
 * De ENE sleutel die een lezer a priori kent (§4.2, de `GEO_MANIFEST_KEY`-regel):
 * alles anders wordt uit het manifest geresolved, zodat producent en consument
 * nooit op een hardgecodeerde bestandsnaam kunnen driften.
 */
export const LISTINGS_MANIFEST_KEY = 'listings/manifest.json';
/**
 * Contract §1/§4.2 — de sleutelvorm van een feedbestand:
 * `listings/<transaktion>/feed-<generation>-<sha16>.json`. Zonder `g`-vlag: een
 * globale regex draagt `lastIndex` en zou bij hergebruik stil missen.
 */
export const FEED_KEY_REGEX = /^listings\/(kauf|miete)\/feed-(\d+)-([0-9a-f]{16})\.json$/;
/**
 * Contract §1 — de sleutelvorm van een publieke beeldvariant:
 * `listings/<transaktion>/bilder/<objektId>/<stempel>-{hero,card}.webp`.
 * `<stempel>` is de 16 hex van `bildStempel` (§5), `{hero,card}` zijn
 * {@link VERMARKTUNG_BILD_VARIANTEN}. Zonder `g`-vlag, om dezelfde reden als
 * hierboven.
 *
 * 🔴 **NIEUW EN NIET UIT HET CONTRACT: de vorm van `<objektId>`.** §1 zet dat id
 * als PADSEGMENT in de sleutel, maar §7 begrenst `id` alleen op lengte (≤ 64) en
 * zegt niets over de tekens. Een id met een `/` of een `..` erin breekt de
 * gesloten sleutelruimte van §1 open, en het opruim-hek van §6 zou zo'n sleutel
 * gewoon doorlaten — hij begint immers met `listings/kauf/`. Daarom hier een
 * nauwe segmentvorm: letters, cijfers, `_` en `-`, en met opzet GEEN punt (dus
 * ook geen `.` of `..`) en geen slash. Dat is een IMPLEMENTATIEGRENS in de zin
 * van §7 en geen contractbelofte: hij mag ruimer worden, maar nooit stil —
 * {@link bildKeyUit} gooit erop, zodat een bron die hem overschrijdt luid
 * stukloopt in plaats van een onopruimbare sleutel te minten. Gemeten: de
 * kauf-kant levert cuid/uuid en past; de ONYX-`objektId` (fase B) is ongemeten
 * en is als bevinding gemeld.
 */
export const BILD_KEY_REGEX = /^listings\/(kauf|miete)\/bilder\/([A-Za-z0-9_-]{1,64})\/([0-9a-f]{16})-(hero|card)\.webp$/;
/**
 * Contract §1 — `<sha16>` is de eerste **16** hex van sha256 over de canonieke
 * feed-bytes (§2.5), met zoveel woorden NIET 8: de feed mint per generatie een
 * nieuwe sleutel (de hoogste sleutelomloop van het bucket) en een botsing zou een
 * jaar lang een oude feed `immutable` uitserveren zonder fout of alarm. Het
 * tiles-precedent (`sha256.slice(0, 8)`, kwartaal-vintage) is hier geen maat.
 *
 * Woont HIER en niet bij de aanroeper: wie zelf mag afkappen, kapt ooit op 8 af.
 */
const FEED_SHA16_LAENGE = 16;
/**
 * Contract §1 — de feed-sleutel, en de ENIGE plek waar hij ontstaat.
 *
 * Neemt de VOLLEDIGE sha256 — dezelfde waarde die als `sha256` in het
 * manifest-slot gaat (§4.2) — en kapt zelf af op {@link FEED_SHA16_LAENGE}.
 * Bewust niet «geef mij de sha16»: dan ligt de afkap-regel bij elke aanroeper en
 * is «16, niet 8» een afspraak in plaats van een grendel. Dit wijkt af van de
 * letter van de bouworder (die noemt `sha16` als parameter) — gemeld, niet stil.
 *
 * Gooit op elke invoer die geen geldige key oplevert; de laatste toets is
 * {@link FEED_KEY_REGEX} zelf. Gooien en niet «een key teruggeven die de lezer
 * afwijst»: het manifest wijst er een jaar lang `immutable` naar.
 */
export function feedKeyUit(transaktion, generation, sha256) {
    if (!Number.isSafeInteger(generation) || generation < 0) {
        throw new Error(`vermarktung_feed_key_generation_ungueltig: ${String(generation)}`);
    }
    if (!SHA256_RE.test(sha256)) {
        throw new Error('vermarktung_feed_key_sha256_ungueltig');
    }
    const kort = sha256.slice(0, FEED_SHA16_LAENGE);
    const key = `listings/${transaktion}/feed-${generation}-${kort}.json`;
    if (!FEED_KEY_REGEX.test(key)) {
        throw new Error(`vermarktung_feed_key_ungueltig: ${key}`);
    }
    return key;
}
/**
 * Contract §1 — de parse-pin `^feed-(\d+)-([0-9a-f]{16})\.json$`, als functie.
 *
 * Het opruim-hek (§6) leest de generatie UIT DE NAAM — «de generatie in de
 * sleutelnaam is de maat, zo blijft staan wat een parallelle jongere job zojuist
 * schreef» — en dat lezen mag nooit langs een tweede, eigen regex lopen.
 *
 * `null` = geen feed-sleutel. Ook bij een generatie die geen veilig geheel getal
 * meer is: `\d+` kent geen bovengrens, en een onleesbare generatie mag geen
 * opruim-besluit dragen — fail-closed, het bestand blijft dan staan. Een gepadde
 * generatie (`feed-007-…`) parst wél, precies zoals de parse-pin zegt;
 * {@link feedKeyUit} maakt hem nooit (§1: decimaal zonder padding).
 */
export function feedKeyOntleden(key) {
    const treffer = FEED_KEY_REGEX.exec(key);
    if (treffer === null)
        return null;
    const generation = Number(treffer[2]);
    if (!Number.isSafeInteger(generation))
        return null;
    return {
        transaktion: treffer[1],
        generation,
        sha16: treffer[3],
    };
}
/**
 * Contract §1/§5 — de sleutel van één publieke beeldvariant, en de ENIGE plek
 * waar hij ontstaat. Dezelfde eigenschap als {@link feedKeyUit}: de uitkomst
 * wordt tegen {@link BILD_KEY_REGEX} getoetst, dus een sleutel die de lezerskant
 * (en het opruim-hek van §6) niet herkent kan hier niet uitkomen.
 *
 * `stempel` komt uit `bildStempel` (§5, `vermarktung-feed-stempel.ts`) — dat
 * bestand kent `node:crypto` en hangt daarom alleen in de root-barrel; deze
 * bouwer is puur en hoort bij de sleutelruimte.
 */
export function bildKeyUit(transaktion, objektId, stempel, variant) {
    const key = `listings/${transaktion}/bilder/${objektId}/${stempel}-${variant}.webp`;
    if (!BILD_KEY_REGEX.test(key)) {
        throw new Error(`vermarktung_bild_key_ungueltig: ${key}`);
    }
    return key;
}
// ─────────────────────────── Manifest (contract §4.2) ───────────────────────
/** Contract §4.2 — één slot van het manifest (of `null`, zie hieronder). */
export const feedSlotSchema = z
    .object({
    key: z.string().regex(FEED_KEY_REGEX),
    sha256: z.string().regex(SHA256_RE),
    count: z.number().int().nonnegative(),
    generation: z.number().int().nonnegative(),
})
    .strict();
/**
 * Contract §4.2 — twee slots, **beide altijd aanwezig**; `null` = deze
 * transaktie is nog nooit gepubliceerd (lezer rendert leeg, geen 404) — het
 * `geoAssetsSchema.timeseries`-precedent. Een slot mag een OUDERE `generation`
 * dragen dan het manifest: dat is de last-good-regel, en zo herkent een lezer
 * een stale slot. Gedeeld door de drie manifestschema's: de slotvorm is in v1
 * en v2 dezelfde.
 */
const manifestFeedsSchema = z
    .object({
    kauf: feedSlotSchema.nullable(),
    miete: feedSlotSchema.nullable(),
})
    .strict();
/**
 * Het prefix van de key MOET bij het slot horen: een kauf-slot dat naar
 * `listings/miete/…` wijst is de mismatch die §2.1 met een pin verbiedt, en
 * `FEED_KEY_REGEX` alleen ziet hem niet (beide prefixen zijn geldig).
 */
const manifestRegel = (manifest, ctx) => {
    for (const transaktion of VERMARKTUNG_TRANSAKTIONEN) {
        const slot = manifest.feeds[transaktion];
        if (slot === null)
            continue;
        if (!slot.key.startsWith(`listings/${transaktion}/`)) {
            ctx.addIssue({
                code: 'custom',
                path: ['feeds', transaktion, 'key'],
                message: 'praefix_ungleich_slot',
            });
        }
    }
};
/** Het manifest in de BEVROREN v1-vorm — uitsluitend om te LEZEN. */
export const feedManifestSchemaV1 = z
    .object({
    contractVersion: z.literal(1),
    generation: z.number().int().nonnegative(),
    generatedAt: feedZeitstempelSchema,
    feeds: manifestFeedsSchema,
})
    .strict()
    .superRefine(manifestRegel);
/**
 * Het manifest, SCHRIJFschema (v2). De vorm is die van v1 — alleen de versie
 * beweegt, en dat MOET: een lezer die het manifest leest beslist op dat getal
 * of hij de feedbestanden kan lezen waar het naar wijst (contract §10).
 */
export const feedManifestSchema = z
    .object({
    contractVersion: z.literal(2),
    generation: z.number().int().nonnegative(),
    generatedAt: feedZeitstempelSchema,
    feeds: manifestFeedsSchema,
})
    .strict()
    .superRefine(manifestRegel);
/**
 * De LEESUNIE van het manifest (contract §10): versie 1 óf 2. De grendel (§4.1)
 * leest hiermee de etag-basis — zonder deze unie zou de EERSTE v2-generatie op
 * een omgeving met een v1-manifest (staging) in een worp eindigen
 * (`vermarktung_manifest_ungueltig`) en nooit meer publiceren. Een onbekende
 * versie (`3`) faalt dicht: de lezer houdt dan zijn laatste goede stand.
 */
export const feedManifestLeesSchema = z
    .object({
    contractVersion: z.union([z.literal(1), z.literal(2)]),
    generation: z.number().int().nonnegative(),
    generatedAt: feedZeitstempelSchema,
    feeds: manifestFeedsSchema,
})
    .strict()
    .superRefine(manifestRegel);
// ─────────────────── Canonieke serialisatie (contract §2.5) ─────────────────
/**
 * Contract §2.5: `JSON.stringify` met GESORTEERDE objectsleutels op elk niveau,
 * geen witruimte, UTF-8. `generation`/`generatedAt` tellen mee — elke generatie
 * is een nieuw bestand; de pointer beslist, niet de gelijkheid van inhoud.
 *
 * 🔴 **VERBODEN: spread-serialisatie.** Elk veld wordt afzonderlijk opgesomd
 * (de INV-1-les uit ONYX band 3: een spread liet een hash door). Het blok
 * hieronder staat tussen twee merktekens en `vermarktung-feed.test.ts` leest
 * zijn eigen bron en weigert er een `.`-drieslag in.
 */
// ── KANONIEK-BEGIN (contract §2.5 — geen spread in dit blok) ─────────────────
function sorteerDiep(waarde) {
    if (Array.isArray(waarde)) {
        const uit = [];
        for (let i = 0; i < waarde.length; i += 1)
            uit.push(sorteerDiep(waarde[i]));
        return uit;
    }
    if (waarde !== null && typeof waarde === 'object') {
        const bron = waarde;
        const uit = {};
        const sleutels = Object.keys(bron).sort();
        for (let i = 0; i < sleutels.length; i += 1) {
            const sleutel = sleutels[i];
            const veld = bron[sleutel];
            if (veld === undefined)
                continue;
            uit[sleutel] = sorteerDiep(veld);
        }
        return uit;
    }
    return waarde;
}
export function kanonischeFeedTekst(envelop) {
    return JSON.stringify(sorteerDiep(envelop));
}
export function kanonischeFeedBytes(envelop) {
    return new TextEncoder().encode(kanonischeFeedTekst(envelop));
}
// ── KANONIEK-EINDE ──────────────────────────────────────────────────────────
